import type {
  AssistantMessage,
  AttachedFile,
  Operator,
  ServerMessage,
} from '../state/types';
import { i18n } from '../i18n/nl';

/**
 * Genereert een plain-text transcript uit het volledige gespreksverloop
 * en triggert een browser-download. Server- én assistent-berichten
 * worden chronologisch samengevoegd op timestamp — zo verschijnt de
 * virtuele-assistent-uitwisseling (naam + e-mail) op de juiste plek
 * in het verhaal, vóór de echte medewerker. Customer-replies uit beide
 * bronnen worden gelabeld als "Jij" — vanuit gebruikersperspectief is
 * er één auteur.
 */
export function downloadTranscript(args: {
  serverMessages: ServerMessage[];
  assistantMessages: AssistantMessage[];
  operator: Operator | null;
  orgName: string;
}): void {
  const { serverMessages, assistantMessages, operator, orgName } = args;
  const text = buildTranscriptText({
    serverMessages,
    assistantMessages,
    operator,
    orgName,
    now: new Date(),
  });
  triggerDownload(text, makeFilename(orgName));
}

/**
 * Pure builder — losgekoppeld van de DOM-side-effect zodat-ie
 * unit-testbaar is zonder browser.
 */
export function buildTranscriptText(args: {
  serverMessages: ServerMessage[];
  assistantMessages: AssistantMessage[];
  operator: Operator | null;
  orgName: string;
  now: Date;
}): string {
  const { serverMessages, assistantMessages, operator, orgName, now } = args;

  type TranscriptEntry = {
    timestamp: number;
    label: string;
    text: string;
    files?: AttachedFile[];
  };

  const operatorLabel = operator?.name ?? i18n.transcript.fallbackOperator;
  const entries: TranscriptEntry[] = [];

  for (const m of serverMessages) {
    entries.push({
      timestamp: m.timestamp,
      label:
        m.author === 'operator'
          ? operatorLabel
          : m.author === 'customer'
            ? i18n.transcript.labelJij
            : i18n.transcript.labelSysteem,
      text: m.text,
      files: m.files,
    });
  }
  for (const a of assistantMessages) {
    entries.push({
      timestamp: a.timestamp,
      label:
        a.author === 'assistant'
          ? i18n.transcript.labelDigitaleAssistent
          : i18n.transcript.labelJij,
      text: a.text,
    });
  }
  entries.sort((a, b) => a.timestamp - b.timestamp);

  const lines: string[] = [];
  lines.push(i18n.transcript.titleFor(orgName));
  lines.push(
    `${i18n.transcript.dateLabel}: ${now.toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })}`,
  );
  if (operator) lines.push(i18n.transcript.medewerkerLabel(operator.name, operator.role));
  lines.push('—'.repeat(42));
  lines.push('');

  for (const e of entries) {
    const ts = new Date(e.timestamp).toLocaleTimeString('nl-NL', {
      hour: '2-digit',
      minute: '2-digit',
    });
    lines.push(`[${ts}] ${e.label}:`);
    if (e.text) lines.push(e.text);
    if (e.files) {
      for (const f of e.files) {
        lines.push(i18n.transcript.fileLine(f.name, f.sizeLabel));
      }
    }
    lines.push('');
  }

  lines.push('—'.repeat(42));
  lines.push(i18n.transcript.end);

  return lines.join('\n');
}

function makeFilename(orgName: string): string {
  const dateStr = new Date().toISOString().slice(0, 10);
  return `chat-${orgName.toLowerCase()}-${dateStr}.txt`;
}

function triggerDownload(text: string, filename: string): void {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
